'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { userProfileService, UserProfile } from '@/lib/services/userProfileService';
import { userMessageService } from '@/lib/services/userMessageService';
import { userServiceRequestService } from '@/lib/services/userServiceRequestService';
import ProfileManagement from './components/ProfileManagement';
import ServiceRequests from './components/ServiceRequests';
import BookingHistory from './components/BookingHistory';
import DocumentUploads from './components/DocumentUploads';
import MessagingCenter from './components/MessagingCenter';
import PreferencesEditor from './components/PreferencesEditor';

export default function UserDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [application, setApplication] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'profile' | 'requests' | 'bookings' | 'documents' | 'messages' | 'preferences'>('overview');
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeRequestsCount, setActiveRequestsCount] = useState(0);

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/admin/login');
    }
  }, [authLoading, user, router]);

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      const profileData = await userProfileService.getProfileByUserId(user!.id);
      
      if (!profileData) {
        setLoading(false);
        return;
      }

      setProfile(profileData);

      const applicationData = await userProfileService.getApplicationByProfileId(profileData.id!);
      setApplication(applicationData);

      const unread = await userMessageService.getUnreadCount(profileData.id!);
      setUnreadCount(unread);

      const activeRequests = await userServiceRequestService.getActiveRequests(profileData.id!);
      setActiveRequestsCount(activeRequests.length);
    } catch (error: any) {
      console.error('Failed to load user data:', error.message);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-brand-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-body-text">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-card p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-dark-navy mb-2">No User Profile Found</h2>
          <p className="text-body-text mb-6">You do not have an active user profile. Please contact support if you believe this is an error.</p>
          <button
            onClick={() => router.push('/home-page')}
            className="px-6 py-3 bg-brand-purple text-white rounded-lg hover:bg-opacity-90 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Review' },
      under_review: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Under Review' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
      on_hold: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'On Hold' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-brand-purple text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">User Dashboard</h1>
              <p className="text-white/80">Welcome back, {application?.firstName}!</p>
            </div>
            <button
              onClick={() => router.push('/home-page')}
              className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
              { id: 'profile', label: 'Profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
              { id: 'requests', label: 'Service Requests', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', badge: activeRequestsCount },
              { id: 'bookings', label: 'Booking History', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
              { id: 'documents', label: 'Documents', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
              { id: 'messages', label: 'Messages', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', badge: unreadCount },
              { id: 'preferences', label: 'Preferences', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`relative py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand-purple text-brand-purple' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                  <span>{tab.label}</span>
                  {tab.badge && tab.badge > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                      {tab.badge}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Application Status Card */}
            <div className="bg-white rounded-2xl shadow-card p-6">
              <h2 className="text-xl font-bold text-dark-navy mb-4">Application Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-body-text mb-2">Current Status</p>
                  {getStatusBadge(application?.status || 'pending')}
                </div>
                <div>
                  <p className="text-sm text-body-text mb-2">Application Date</p>
                  <p className="text-dark-navy font-medium">
                    {application?.createdAt ? new Date(application.createdAt).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Activity Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl shadow-card p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-body-text">Active Requests</p>
                  <svg className="w-8 h-8 text-brand-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-dark-navy">{activeRequestsCount}</p>
              </div>

              <div className="bg-white rounded-2xl shadow-card p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-body-text">Total Bookings</p>
                  <svg className="w-8 h-8 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-dark-navy">{profile.totalBookings || 0}</p>
              </div>

              <div className="bg-white rounded-2xl shadow-card p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-body-text">Completed</p>
                  <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-dark-navy">{profile.completedBookings || 0}</p>
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-white rounded-2xl shadow-card p-6">
              <h2 className="text-xl font-bold text-dark-navy mb-4">Quick Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-body-text">Email</p>
                  <p className="text-dark-navy font-medium">{application?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-body-text">Phone</p>
                  <p className="text-dark-navy font-medium">{application?.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-body-text">Location</p>
                  <p className="text-dark-navy font-medium">
                    {application?.city}, {application?.province}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-body-text">Preferred Contact</p>
                  <p className="text-dark-navy font-medium">{profile.preferredContactMethod || 'Email'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profile' && <ProfileManagement profile={profile} application={application} onUpdate={loadUserData} />}
        {activeTab === 'requests' && <ServiceRequests profileId={profile.id!} />}
        {activeTab === 'bookings' && <BookingHistory profileId={profile.id!} />}
        {activeTab === 'documents' && <DocumentUploads profileId={profile.id!} />}
        {activeTab === 'messages' && <MessagingCenter profileId={profile.id!} onMessageRead={() => setUnreadCount(Math.max(0, unreadCount - 1))} />}
        {activeTab === 'preferences' && <PreferencesEditor profileId={profile.id!} />}
      </div>
    </div>
  );
}